
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from 'lucide-react';

interface LoginFormProps {
  redirectPath: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ redirectPath }) => {
  const { signIn, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setErrorMessage(null);

    try {
      console.log("Tentative de connexion avec:", email);
      const { error } = await signIn(email, password);
      
      if (error) {
        console.error("Erreur de connexion détaillée:", error);
        
        if (error.message === "Invalid login credentials") {
          setErrorMessage("Identifiants invalides. Vérifiez votre email et mot de passe.");
        } else {
          setErrorMessage(error.message || "Vérifiez vos identifiants et réessayez.");
        }
        
        toast({
          title: "Erreur de connexion",
          description: error.message === "Invalid login credentials" 
            ? "Identifiants invalides. Vérifiez votre email et mot de passe."
            : (error.message || "Vérifiez vos identifiants et réessayez."),
          variant: "destructive"
        });
      } else {
        toast({
          title: "Connexion réussie",
          description: "Vous êtes maintenant connecté.",
        });
        
        // Instead of redirecting immediately, wait for profile to be loaded
        // The AuthContext will handle loading the profile, and we'll redirect in a moment
        // No redirect action here - see useEffect below
      }
    } catch (error: any) {
      console.error("Exception lors de la connexion:", error);
      setErrorMessage(error.message || "Une erreur est survenue. Veuillez réessayer.");
      
      toast({
        title: "Erreur de connexion",
        description: error.message || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  // After successful login, the profile will be loaded
  // Redirect to the appropriate page based on the role
  React.useEffect(() => {
    if (profile) {
      console.log("Profile loaded after login:", profile);
      
      // Redirect based on role
      if (profile.role === 'candidat') {
        navigate('/dashboard');
      } else if (profile.role === 'entreprise') {
        navigate('/dashboard');
      } else if (profile.role === 'admin') {
        navigate('/admin');
      } else {
        // Default fallback if role is undefined or not recognized
        navigate('/dashboard');
      }
    }
  }, [profile, navigate]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connexion</CardTitle>
        <CardDescription>
          Entrez vos identifiants pour vous connecter à votre compte.
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleLogin}>
        <CardContent className="space-y-4">
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4 mr-2" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">Mot de passe</Label>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={isLoggingIn}>
            {isLoggingIn ? 'Connexion en cours...' : 'Se connecter'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default LoginForm;
